import {
  boundCheckboxField,
  BoundCheckboxField,
  BoundMultiSelectField,
  boundTextAreaField,
  boundTextField,
  BoundTreeSelectField,
  Button,
  Css,
  FormPageLayout,
  HasIdAndName,
  NestedOption,
  Tooltip,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SaveAttachmentModel } from "src/components/boundAttachments/BoundAttachments";
import { BoundSingleAttachment, formatAttachmentAssets } from "src/components/boundAttachments/BoundSingleAttachment";
import {
  ConstraintItemIncrementalCollectionOp,
  IncrementalCollectionOp,
  SaveGlobalPlanMilestoneInput,
  type Maybe,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  MilestoneCatalogForm_GlobalPlanMilestoneFragment,
  MilestoneCatalogForm_GlobalPlanMilestoneGroupFragment,
  useMilestoneCatalogFormDropdownQuery,
  useMilestoneCatalogFormQuery,
  useSaveGlobalPlanMilestonesMutation,
} from "src/routes/libraries/milestone-catalog/MilestoneCatalogForm.generated";
import { IdOrAddParams, milestonesCatalogPath } from "src/routes/routesDef";
import { addEntityParam } from "src/RouteUrls";
import { mapFormInputToIncrementalOp, queriesResult } from "src/utils";
import { AddConstraintItemModal, ConstraintModalConfig } from "../task-catalog/components/AddConstraintItemModal";
import { LabelledTextWithTooltip } from "../task-catalog/components/TaskCatalogForm";
import { AddAdditionalMilestoneCard } from "./components/AddAdditionalMilestoneCard";
import { AddGlobalPlanMilestoneGroupModal } from "./components/AddGlobalPlanMilestoneGroupModal";

export function MilestoneCatalogForm() {
  const { idOrAdd } = useRouteParams<IdOrAddParams>();
  const isFormNew = idOrAdd === addEntityParam;
  const formQuery = useMilestoneCatalogFormQuery({ variables: { id: idOrAdd }, skip: isFormNew });
  const dropdownQuery = useMilestoneCatalogFormDropdownQuery({});
  return queriesResult([formQuery, dropdownQuery] as const, {
    data: (formData, dropdownData) => (
      <MilestoneCatalogFormView
        globalPlanMilestone={formData?.globalPlanMilestone}
        globalPlanConstraintItems={dropdownData.globalPlanConstraintItems}
        globalPlanMilestoneGroups={dropdownData.globalPlanMilestoneGroups}
        isFormNew={isFormNew}
      />
    ),
  });
}

type MilestoneCatalogFormViewProps = {
  globalPlanMilestone: MilestoneCatalogForm_GlobalPlanMilestoneFragment;
  globalPlanConstraintItems: HasIdAndName[];
  globalPlanMilestoneGroups: MilestoneCatalogForm_GlobalPlanMilestoneGroupFragment[];
  isFormNew: boolean;
};

export function MilestoneCatalogFormView(props: MilestoneCatalogFormViewProps) {
  const { globalPlanConstraintItems, globalPlanMilestone, globalPlanMilestoneGroups, isFormNew } = props;
  const [saveGlobalPlanMilestones] = useSaveGlobalPlanMilestonesMutation();
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const { openModal } = useModal();

  const formState = useFormState({
    config: formValue,
    loading: !isFormNew && !globalPlanMilestone,
    addRules(state) {
      state.homeownerName.rules.push(() =>
        state.isHomeownerVisible.value && !state.homeownerName.value ? "Required" : undefined,
      );
      state.homeownerDescription.rules.push(() =>
        state.isHomeownerVisible.value && !state.homeownerDescription.value ? "Required" : undefined,
      );
      state.homeownerIcon.rules.push(() =>
        state.isHomeownerVisible.value && !state.homeownerIcon.value ? "Required" : undefined,
      );
    },
    init: {
      input: globalPlanMilestone,
      map: (gpm) => {
        const { constraints, groups, id, homeownerIcon, description, ...others } = gpm;
        return {
          id: isFormNew ? undefined : id,
          description: description ?? "",
          constraints: constraints.map((c) => c.id),
          groups: groups.map((group) => group.id),
          isAllDefaultSelected: false,
          // intentionally set to empty array as we never will edit multiple milestones at once
          additionalMilestoneCards: [],
          homeownerIcon: homeownerIcon ? { asset: homeownerIcon } : undefined,
          ...others,
        };
      },
    },
  });

  const onSubmit = useCallback(async () => {
    // removing properties we don't want to send to the backend
    const { additionalMilestoneCards, isAllDefaultSelected, ...others } = formState.value;
    await saveGlobalPlanMilestones({
      variables: {
        input: {
          globalPlanMilestones: [
            mapToInput(others, globalPlanMilestone, isFormNew),
            // merge in the additional milestones if we have them
            ...(additionalMilestoneCards?.nonEmpty
              ? additionalMilestoneCards.map((value) => {
                  return { ...mapToInput(value, globalPlanMilestone, isFormNew) };
                })
              : []),
          ],
        },
      },
      onCompleted: () => {
        triggerNotice({ message: isFormNew ? "A new milestone was created!" : "Milestone was successfully updated." });
      },
    });
    // redirect back to Milestone Catalog Page once created
    navigate(milestonesCatalogPath);
  }, [formState, saveGlobalPlanMilestones, triggerNotice, isFormNew, navigate, globalPlanMilestone]);

  const treeSelectOptions = useMemo(
    () => formatTreeSelectOptions(globalPlanMilestoneGroups ?? []),
    [globalPlanMilestoneGroups],
  );

  const isHomeownerVisible = useComputed(
    () => formState.isHomeownerVisible.value === true,
    [formState.isHomeownerVisible.value],
  );

  const homeownerIconError = useComputed(
    () => formState.homeownerIcon.errors[0],
    [formState.homeownerIcon.errors, formState.homeownerIcon.errors.length],
  );

  const isFormValid = useComputed(() => formState.valid, [formState.valid]);

  return (
    <FormPageLayout
      formState={formState}
      pageTitle={isFormNew ? "Create New Milestone" : "Edit Milestone"}
      breadCrumb={[{ label: "Milestone Catalog", href: milestonesCatalogPath }]}
      cancelAction={{ label: "Cancel", onClick: milestonesCatalogPath }}
      submitAction={{ label: isFormNew ? "Create" : "Save", onClick: onSubmit, disabled: !isFormValid }}
      formSections={[
        ...(isFormNew
          ? [
              {
                rows: [
                  {
                    reactNodeGroups: (
                      <GroupSelectSection
                        formState={formState}
                        treeSelectOptions={treeSelectOptions}
                        openModal={openModal}
                      />
                    ),
                  },
                  {
                    isAllDefaultSelected: boundCheckboxField({
                      label: "Set all as default milestones",
                      onChange: (val) => {
                        formState.isAllDefaultSelected.set(val);
                        formState.isDefault.set(val);
                        formState.additionalMilestoneCards.rows.forEach((milestone) => {
                          // set the additonal milestones to the same value as the parent
                          milestone.isDefault.set(val);
                        });
                      },
                    }),
                  },
                ],
              },
            ]
          : []),
        {
          title: "Blueprint",
          rows: [
            {
              name: boundTextField({
                label: "Milestone Name",
                required: true,
                fullWidth: true,
                placeholder: "Enter milestone name",
              }),
            },
            { description: boundTextAreaField({ label: "Description", placeholder: "Enter a short description" }) },
            {
              reactNodeConstraints: (
                <ConstraintsSection
                  formState={formState}
                  globalPlanConstraintItems={globalPlanConstraintItems}
                  openModal={openModal}
                />
              ),
            },
            {
              reactNodeIsDefault: !isFormNew ? (
                <Tooltip title="Changing this milestone's default status is disabled after the milestone is created for now.">
                  <BoundCheckboxField
                    field={formState.isDefault}
                    label="Set as default milestone"
                    disabled={!isFormNew}
                    onChange={(val) => {
                      formState.isDefault.set(val);
                      formState.isAllDefaultSelected.set(null);
                    }}
                  />
                </Tooltip>
              ) : (
                <BoundCheckboxField
                  field={formState.isDefault}
                  label="Set as default milestone"
                  disabled={!isFormNew}
                  onChange={(val) => {
                    formState.isDefault.set(val);
                    formState.isAllDefaultSelected.set(null);
                  }}
                />
              ),
            },
            { isHomeownerVisible: boundCheckboxField({ label: "Show in the Homeowner Portal" }) },
          ],
        },

        {
          title: "Homeowner Portal",
          rows: [
            {
              homeownerName: boundTextField({
                label: "Milestone Name",
                placeholder: formState.name.value || "Enter milestone name",
                disabled: !isHomeownerVisible,
              }),
            },
            {
              homeownerDescription: boundTextAreaField({
                label: "Description",
                placeholder: "Enter a short description",
                disabled: !isHomeownerVisible,
              }),
            },
            {
              reactNodeHomeownerIcon: isHomeownerVisible ? (
                <div css={Css.df.fdc.gap1.$}>
                  <BoundSingleAttachment
                    field={formState.homeownerIcon}
                    title="Icon *"
                    uppyUploaderProps={{ dragDropHeight: 96, dragDropWidth: 96 }}
                    shouldDeleteField={formState.shouldDeleteHomeownerIcon}
                  />
                  <div css={Css.gray700.sm.$}>Icon should be 60x60px or less</div>
                  {homeownerIconError && <div css={Css.red700.sm.$}>{homeownerIconError}</div>}
                </div>
              ) : null,
            },
          ],
        },
        ...(isFormNew
          ? [
              {
                rows: [
                  {
                    reactNodeAdditionalMilestones: (
                      <AddAdditionalMilestoneCard
                        formState={formState}
                        openModal={openModal}
                        globalPlanConstraintItems={globalPlanConstraintItems}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ]}
    />
  );
}

export type SaveAdditionalMilestoneCardFormValue = Pick<
  SaveGlobalPlanMilestoneInput,
  "id" | "archived" | "isHomeownerVisible" | "description" | "homeownerName" | "homeownerDescription"
> & {
  constraints: Maybe<Array<string>>;
  name: Maybe<string>;
  isDefault: Maybe<boolean>;
  groups: Maybe<Array<string>>;
  homeownerIcon?: SaveAttachmentModel;
  shouldDeleteHomeownerIcon?: boolean;
};

export type SaveGlobalPlanMilestoneFormValue = Pick<
  SaveGlobalPlanMilestoneInput,
  | "id"
  | "archived"
  | "isDefault"
  | "name"
  | "isHomeownerVisible"
  | "description"
  | "homeownerName"
  | "homeownerDescription"
> & {
  constraints: Maybe<Array<string>>;
  groups: Maybe<Array<string>>;
  isAllDefaultSelected: Maybe<boolean>;
  additionalMilestoneCards: SaveAdditionalMilestoneCardFormValue[];
  homeownerIcon?: SaveAttachmentModel;
  shouldDeleteHomeownerIcon?: boolean;
};

export const additionalMilestoneCardConfig: ObjectConfig<SaveAdditionalMilestoneCardFormValue> = {
  constraints: { type: "value" },
  name: { type: "value", rules: [required] },
  isDefault: { type: "value" },
  groups: { type: "value" },
  isHomeownerVisible: { type: "value" },
  description: { type: "value" },
  homeownerName: { type: "value" },
  homeownerDescription: { type: "value" },
  homeownerIcon: { type: "value" },
  shouldDeleteHomeownerIcon: { type: "value" },
};

export const formValue: ObjectConfig<SaveGlobalPlanMilestoneFormValue> = {
  name: { type: "value", rules: [required] },
  isDefault: { type: "value" },
  isAllDefaultSelected: { type: "value" },
  constraints: { type: "value" },
  groups: { type: "value", rules: [required] },
  additionalMilestoneCards: { type: "list", config: additionalMilestoneCardConfig },
  isHomeownerVisible: { type: "value" },
  description: { type: "value" },
  homeownerName: { type: "value" },
  homeownerDescription: { type: "value" },
  homeownerIcon: { type: "value" },
  shouldDeleteHomeownerIcon: { type: "value" },
};

export function formatTreeSelectOptions(
  groups: MilestoneCatalogForm_GlobalPlanMilestoneGroupFragment[],
): NestedOption<HasIdAndName>[] {
  const groupsByBusinessFunctionType = groups.groupBy((group) => group.businessFunctionType.name);
  return (
    Object.keys(groupsByBusinessFunctionType)
      .map((bf) => ({
        id: bf,
        name: bf,
        children: groupsByBusinessFunctionType[bf]?.map((group) => ({ id: group.id, name: group.name })),
      }))
      // we only want to show business function types that have groups
      .filter((bf) => bf.children?.length)
      .sortByKey("name")
  );
}

// one-off helper to format constraints since it takes in a differnt type of Incremental Collection type
function mapToConstraintInput(constraints: Maybe<string[]>, existingValues: HasIdAndName[]) {
  // if we dont have an input then return nothing
  if (!constraints) return [];

  const existingValuesAsIds = existingValues?.map((ci) => ci.id) ?? [];

  const removedValues =
    existingValues
      ?.filter((existingValue) => !constraints?.find((input) => input === existingValue.id))
      .map((removedCi) => ({ id: removedCi.id, op: ConstraintItemIncrementalCollectionOp.Remove })) ?? [];

  const addedValues = constraints
    .filter((input) => !existingValuesAsIds.includes(input ?? ""))
    .map((addedCi) => ({ id: addedCi, op: ConstraintItemIncrementalCollectionOp.Include }));

  return [...removedValues, ...addedValues];
}

function mapToInput(
  formValue:
    | Omit<SaveGlobalPlanMilestoneFormValue, "additionalMilestoneCards" | "isAllDefaultSelected">
    | SaveAdditionalMilestoneCardFormValue,
  data: MilestoneCatalogForm_GlobalPlanMilestoneFragment | undefined,
  isFormNew: boolean,
): SaveGlobalPlanMilestoneInput {
  const {
    groups,
    constraints,
    id,
    isHomeownerVisible,
    homeownerName,
    homeownerIcon,
    shouldDeleteHomeownerIcon,
    homeownerDescription,
    ...others
  } = formValue;

  const groupsInput = mapFormInputToIncrementalOp(groups, data?.groups, {
    existingValueToId: (group) => group.id,
    removedMapper: (removedGroup) => ({ id: removedGroup.id, op: IncrementalCollectionOp.Remove }),
    addedMapper: (addedId) => ({ id: addedId, op: IncrementalCollectionOp.Include }),
  });

  const constraintsInput = mapToConstraintInput(constraints, data?.constraints ?? []);

  const homeownerIconInput = formatAttachmentAssets(homeownerIcon, shouldDeleteHomeownerIcon, data?.homeownerIcon?.id);

  return {
    id: !isFormNew ? data?.id : undefined,
    ...(groupsInput?.nonEmpty && { groups: groupsInput }),
    ...(constraintsInput?.nonEmpty && { constraints: constraintsInput }),
    isHomeownerVisible,
    ...(isHomeownerVisible && { homeownerName, homeownerDescription, homeownerIcon: homeownerIconInput.asset }),
    ...others,
  };
}

function GroupSelectSection({
  formState,
  treeSelectOptions,
  openModal,
}: {
  formState: ObjectState<SaveGlobalPlanMilestoneFormValue>;
  treeSelectOptions: NestedOption<HasIdAndName>[];
  openModal: ReturnType<typeof useModal>["openModal"];
}) {
  return (
    <div css={Css.df.fdc.gap1.$}>
      <LabelledTextWithTooltip
        label="Group *"
        tooltip="One or more tags that identifies which group or groups that this particular milestone belongs to (example: “Design Review”)."
      >
        <div css={Css.mt1.$}>
          <BoundTreeSelectField
            field={formState.groups}
            label="Group"
            labelStyle="hidden"
            fullWidth
            defaultCollapsed
            options={treeSelectOptions}
            onSelect={(options) => formState.groups.set(options.leaf.values)}
          />
        </div>
      </LabelledTextWithTooltip>
      <Button
        label="Add New"
        variant="text"
        onClick={() => openModal({ content: <AddGlobalPlanMilestoneGroupModal formState={formState} /> })}
      />
    </div>
  );
}

function ConstraintsSection({
  formState,
  globalPlanConstraintItems,
  openModal,
}: {
  formState: ObjectState<SaveGlobalPlanMilestoneFormValue>;
  globalPlanConstraintItems: HasIdAndName[];
  openModal: ReturnType<typeof useModal>["openModal"];
}) {
  return (
    <div css={Css.df.fdc.gap1.$}>
      <BoundMultiSelectField
        field={formState.constraints}
        label="Constraints"
        options={globalPlanConstraintItems ?? []}
      />
      <Button
        label="Add New"
        variant="text"
        onClick={() =>
          openModal({
            content: (
              <AddConstraintItemModal
                constraintType={ConstraintModalConfig.ConstraintItem}
                fieldState={formState.constraints}
              />
            ),
          })
        }
      />
    </div>
  );
}
